/* S2400 purchase UI. Shopify owns variant resolution, media, inventory and cart requests. */
if (!customElements.get('s2400-configurator')) {
  customElements.define('s2400-configurator', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info');
      if (!this.root || this.controller) return;
      this.config = JSON.parse(this.querySelector('[data-s2400-config]').textContent);
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.money = new Intl.NumberFormat(this.config.locale || document.documentElement.lang || 'en', {
        style: 'currency', currency: this.config.currency || 'EUR'
      });
      this.pending = false;
      this.failed = false;
      this.root.addEventListener('change', (event) => {
        if (event.target.closest('variant-selects')) {
          this.pending = true; this.failed = false;
          this.updateStatus(); this.syncButton(); this.syncConsult();
        }
      }, {...options, capture: true});
      this.root.addEventListener('change', (event) => {
        if (event.target.closest('[data-sp-inline-accessories]') || event.target.name === 'quantity') this.sync();
      }, options);
      this.root.addEventListener('sunpura:variant-rendered', () => {
        this.pending = false; this.failed = false; this.sync();
      }, options);
      this.root.addEventListener('sunpura:variant-error', () => {
        this.pending = false; this.failed = true; this.updateStatus(); this.syncButton(); this.syncConsult();
      }, options);
      this.root.addEventListener('click', (event) => {
        if (event.target.closest('[data-s2400-retry]')) {
          this.root.querySelector('variant-selects input:checked')?.dispatchEvent(new Event('change', {bubbles: true}));
        }
        if (event.target.closest('[data-s2400-mobile-add]')) {
          const primary = this.primary;
          if (primary && !primary.disabled && primary.getAttribute('aria-disabled') !== 'true' && !this.pending && !this.failed) primary.click();
        }
      }, options);
      // Observe the native submit button only; never observe markup that this component rewrites.
      this.buttonObserver = new MutationObserver(() => this.syncButton());
      if (this.primary) this.buttonObserver.observe(this.primary, {attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'], childList: true, subtree: true, characterData: true});
      this.root.addEventListener('submit', (event) => {
        // form.id can be shadowed by Shopify's input[name="id"]. Read the attribute.
        if (event.target.getAttribute('id') === 'product-form-' + this.dataset.section && (this.config.buy_enabled === false || this.pending || this.failed)) {event.preventDefault(); event.stopImmediatePropagation();}
      }, {...options, capture: true});
      this.onScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {this.scrollFrame = 0; this.syncBar();});
      };
      window.addEventListener('scroll', this.onScroll, {...options, passive: true});
      window.addEventListener('resize', this.onScroll, {...options, passive: true});
      this.host = this.closest('.sp-s2400-product-information');
      this.visibilityObserver = new IntersectionObserver(entries => {
        this.host?.classList.toggle('is-in-view', entries[0].isIntersecting);
      });
      this.visibilityObserver.observe(this.root);
      this.sync();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.buttonObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.host?.classList.remove('is-in-view');
      cancelAnimationFrame(this.scrollFrame);
    }
    get primary() {return this.root.querySelector('#ProductSubmitButton-' + this.dataset.section);}
    format(cents) {return this.money.format(Number(cents || 0) / 100);}
    text(node, value) {if (node && node.textContent !== String(value)) node.textContent = String(value);}
    getState() {
      const items = [];
      this.root.querySelectorAll('.sp-s2400-inline-addon').forEach(row => {
        const check = row.querySelector('input[type="checkbox"]');
        if (!check) return;
        const option = row.querySelector('select')?.selectedOptions[0];
        if (option) {
          check.value = option.value;
          check.dataset.price = option.dataset.price;
          check.dataset.variantTitle = option.dataset.variantTitle;
          const compare = row.querySelector('[data-sp-accessory-compare]');
          if (compare) {
            compare.hidden = Number(option.dataset.comparePrice) <= Number(option.dataset.price);
            this.text(compare, this.format(option.dataset.comparePrice));
          }
        }
        this.text(row.querySelector('[data-sp-accessory-price]'), '+ ' + this.format(check.dataset.price));
        row.classList.toggle('is-selected', check.checked && !check.disabled);
        if (!check.checked || check.disabled) return;
        const variantTitle = check.dataset.variantTitle;
        const title = check.dataset.productTitle + (variantTitle && !/^(default title|standaardtitel)$/i.test(variantTitle) ? ' · ' + variantTitle : '');
        items.push({id: Number(check.value), quantity: 1, price: Number(check.dataset.price), title, properties: {_sp_role: 'paid_addon', _sp_source: 's2400_pdp', ...(check.dataset.spVatAddon === 'true' ? window.SunpuraVAT?.addonProperties(this.root.querySelector('product-form form')) || {} : {})}});
      });
      return {items, count: items.length, totalCents: items.reduce((sum, item) => sum + item.price * item.quantity, 0)};
    }
    sync() {
      const data = this.root.querySelector('variant-selects [data-selected-variant]');
      const variant = data ? JSON.parse(data.textContent) : this.config.single_variant;
      this.variant = variant;
      const state = this.getState();
      this.root.querySelectorAll('[data-sp-accessory-count]').forEach(n => this.text(n, state.count));
      this.root.querySelectorAll('[data-sp-accessory-total]').forEach(n => this.text(n, this.format(state.totalCents)));
      const options = variant?.options || [];
      const capacity = (options[0] || variant?.option1 || '').split('(')[0].trim();
      const numeric = parseFloat(capacity.replace(',', '.'));
      const count = [2.4,4.8,7.2,9.6].includes(numeric) ? Math.round(numeric / 2.4) : null;
      this.root.querySelectorAll('[data-s2400-capacity], [data-s2400-mobile-capacity]').forEach(n => this.text(n, capacity || '—'));
      this.root.querySelectorAll('[data-s2400-battery-count]').forEach(n => this.text(n, count || '—'));
      this.text(this.root.querySelector('[data-s2400-system-solar]'), options.slice(1).join(' · ') || variant?.option2 || '—');
      const quantity = Number(this.root.querySelector('product-form [name="quantity"], .product-form__quantity [name="quantity"]')?.value || 1);
      const price = variant ? this.format(variant.price * quantity + state.totalCents) : '—';
      this.root.querySelectorAll('[data-s2400-system-price], [data-s2400-mobile-price]').forEach(n => this.text(n, price));
      const list = this.root.querySelector('[data-s2400-addons-list]');
      const row = this.root.querySelector('[data-s2400-addons-row]');
      if (list && row) {
        row.hidden = !state.items.length;
        const signature = JSON.stringify(state.items);
        if (this.addonSignature !== signature) {
          this.addonSignature = signature;
          list.replaceChildren(...state.items.map(item => {
            const line = document.createElement('div'); line.className = 'sp-s2400-system-summary__addon';
            const name = document.createElement('strong'); name.textContent = item.title;
            const price = document.createElement('small'); price.textContent = '+ ' + this.format(item.price);
            line.append(name, price); return line;
          }));
        }
      }
      this.updateStatus(); this.syncButton(); this.syncConsult(); this.syncBar();
    }
    syncConsult() {
      const link = this.root.querySelector('[data-s2400-consult]');
      if (!link || !link.dataset.baseUrl.startsWith('mailto:')) return;
      const url = new URL(link.dataset.baseUrl);
      const valid = !this.pending && !this.failed && this.variant;
      const selection = valid ? this.variant.options.join(' · ') : this.config.unknown;
      const productUrl = new URL(this.root.dataset.url, window.location.origin);
      if (valid) productUrl.searchParams.set('variant', this.variant.id);
      const addons = valid ? this.getState().items.map(item => item.title).join('; ') : '';
      // Open an editable email draft. No message is sent by the theme.
      url.searchParams.set('subject', this.config.consult_subject);
      url.searchParams.set('body', `${this.config.variant_label}: ${selection}${addons ? '\n' + addons : ''}\n${this.config.product_link}: ${productUrl.href}`);
      link.href = url.href.replace(/\+/g, '%20');
    }
    updateStatus() {
      const status = this.root.querySelector('[data-s2400-status]');
      if (status) {
        status.hidden = !this.pending && !this.failed;
        status.classList.toggle('is-error', this.failed);
        this.text(status.querySelector('[data-s2400-status-text]'), this.failed ? this.config.update_error : this.config.updating);
        status.querySelector('[data-s2400-retry]').hidden = !this.failed;
      }
      const summary = this.root.querySelector('.sp-s2400-system-summary');
      summary?.setAttribute('aria-busy', String(this.pending));
      summary?.classList.toggle('is-updating', this.pending || this.failed);
    }
    syncButton() {
      const primary = this.primary;
      const mobile = this.root.querySelector('[data-s2400-mobile-add]');
      if (!primary || !mobile) return;
      mobile.disabled = primary.disabled || primary.getAttribute('aria-disabled') === 'true' || this.pending || this.failed;
      mobile.setAttribute('aria-disabled', String(mobile.disabled));
      this.text(mobile, this.pending ? this.config.updating : primary.querySelector('span')?.textContent.trim() || primary.textContent.trim());
    }
    syncBar() {
      const bar = this.root.querySelector('[data-s2400-mobile-atc]');
      if (!bar) return;
      const show = this.config.buy_enabled !== false && window.innerWidth <= 749 && this.primary?.getBoundingClientRect().bottom < 0 && this.root.getBoundingClientRect().bottom > 120 && !document.body.classList.contains('overflow-hidden') && !document.querySelector('dialog[open]');
      bar.classList.toggle('is-visible', Boolean(show));
      bar.setAttribute('aria-hidden', String(!show));
      bar.inert = !show;
    }
  });
}
